/**
 * ClaimProfileSection — "Claim or Confirm This Profile" (public, no login).
 * Tied to the exact plant slug/P-location. Submissions require email
 * confirmation before entering the Admin Vault review queue. Submitting
 * NEVER changes the listing's status, categories, or badges.
 */
import React, { useEffect, useRef, useState } from "react";
import { X, BadgeCheck, Upload } from "lucide-react";
import api, { fmtErr } from "../api";

const MAX_FILE = 10 * 1024 * 1024;
const OK_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/webp"];

export function ClaimProfileSection({ m, lang }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <section className="container-cd py-6" style={{ borderBottom: "1px solid var(--line)" }} data-testid="claim-section">
        <div className="cd-card p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="font-heading font-semibold text-base">
              {lang === "es" ? "¿Esta es tu planta?" : "Is this your plant?"}
            </div>
            <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
              {lang === "es"
                ? "Confirma la ubicación, propone capacidades y comparte un documento privado. Esto no cambia el estatus de tu listado."
                : "Confirm the location, propose capabilities and share one private document. This does not change your listing's status."}
            </div>
          </div>
          <button className="btn btn-primary whitespace-nowrap" onClick={() => setOpen(true)} data-testid="claim-open-btn">
            <BadgeCheck size={15} />{" "}
            {lang === "es" ? "Reclamar o confirmar este perfil" : "Claim or Confirm This Profile"}
          </button>
        </div>
      </section>
      {open ? <ClaimModal m={m} lang={lang} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function ClaimModal({ m, lang, onClose }) {
  const es = lang === "es";
  const [taxonomy, setTaxonomy] = useState([]);
  const [locOk, setLocOk] = useState("correct");
  const [corrections, setCorrections] = useState("");
  const [caps, setCaps] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [file, setFile] = useState(null);
  const [fileErr, setFileErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const honeypotRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/capabilities");
        setTaxonomy((data || []).map((c) => c.name));
      } catch { /* non-blocking */ }
    })();
  }, []);

  const address = m.plant_address || m.street_address || m.address || "";
  const immexRef = m.immex_program_reference || m.immex_number || "";

  function onFile(e) {
    setFileErr("");
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (!OK_TYPES.includes(f.type)) {
      setFileErr(es ? "Solo PDF, PNG, JPG o WEBP." : "Only PDF, PNG, JPG or WEBP.");
      setFile(null); return;
    }
    if (f.size > MAX_FILE) {
      setFileErr(es ? "Máximo 10 MB." : "10 MB max.");
      setFile(null); return;
    }
    setFile(f);
  }

  function toggleCap(c) {
    setCaps((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  }

  async function submit(e) {
    e.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("plant_slug", m.slug);
      fd.append("location_confirmed", locOk);
      fd.append("corrections", corrections);
      fd.append("proposed_capabilities", JSON.stringify(caps));
      fd.append("contact_name", name);
      fd.append("contact_role", role);
      fd.append("contact_email", email);
      fd.append("contact_phone", phone);
      fd.append("lang", lang);
      fd.append("website", honeypotRef.current?.value || "");
      if (file) fd.append("file", file);
      const { data } = await api.post("/claims/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(data);
    } catch (e2) {
      setErr(fmtErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200 }} data-testid="claim-overlay" />
      <div
        role="dialog"
        className="bg-white"
        style={{
          position: "fixed", top: "4vh", left: 0, right: 0, margin: "0 auto",
          maxWidth: 680, maxHeight: "92vh", overflowY: "auto",
          padding: "26px 24px 32px", zIndex: 210, border: "1px solid var(--ink)",
        }}
        data-testid="claim-modal"
      >
        <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 12, right: 12, minWidth: 32, minHeight: 32 }} data-testid="claim-close">
          <X size={18} />
        </button>

        {done ? (
          <div className="py-8 text-center" data-testid="claim-success">
            <div className="overline" style={{ color: "var(--orange)" }}>
              {es ? "REFERENCIA" : "REFERENCE"} · <span data-testid="claim-reference-id">{done.reference_id}</span>
            </div>
            <div className="font-display text-3xl mt-3">
              {es ? "Revisa tu correo." : "Check your email."}
            </div>
            <p className="mt-4 text-sm max-w-md mx-auto" style={{ color: "var(--ink-2)" }}>
              {es
                ? "Tu envío está en espera de confirmación por correo. Te enviamos un enlace (válido 7 días). Al confirmarlo, tu envío entra a la cola de revisión."
                : "Your submission is awaiting email confirmation. We sent you a link (valid for 7 days). Once confirmed, your submission enters the review queue."}
            </p>
            <p className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }} data-testid="claim-no-status-change-note">
              {es ? "Esto no cambia el estatus de tu listado." : "This does not change your listing's status."}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <div className="overline" style={{ color: "var(--orange)" }}>
                {es ? "RECLAMAR O CONFIRMAR ESTE PERFIL" : "CLAIM OR CONFIRM THIS PROFILE"}
              </div>
              <div className="font-display text-2xl mt-2">{m.display_name || m.company_name}</div>
              <div className="mt-2 font-mono text-xs space-y-0.5" style={{ color: "var(--ink-2)" }} data-testid="claim-plant-record">
                <div>{es ? "Secuencia" : "Sequence"}: {m.plant_sequence_label || "—"} · {m.slug}</div>
                <div>{es ? "Dirección" : "Address"}: {address || (es ? "No enviada" : "Not submitted")}</div>
                <div>IMMEX: {immexRef || "—"}</div>
              </div>
            </div>

            <div>
              <label className="overline">{es ? "¿La ubicación exacta es correcta?" : "Is this exact plant location correct?"}</label>
              <div className="mt-2 flex gap-4 font-mono text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="loc" checked={locOk === "correct"} onChange={() => setLocOk("correct")} data-testid="claim-loc-correct" />
                  {es ? "Sí, es correcta" : "Yes, it's correct"}
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="loc" checked={locOk === "incorrect"} onChange={() => setLocOk("incorrect")} data-testid="claim-loc-incorrect" />
                  {es ? "No, es incorrecta" : "No, it's incorrect"}
                </label>
              </div>
            </div>

            <div>
              <label className="overline">{es ? "Correcciones a la información (opcional)" : "Company information corrections (optional)"}</label>
              <textarea
                className="cd-textarea mt-2 w-full" rows={3}
                value={corrections} onChange={(e) => setCorrections(e.target.value)}
                placeholder={es ? "Nada se aplica automáticamente — un revisor lo evalúa." : "Nothing is applied automatically — a reviewer evaluates it."}
                data-testid="claim-corrections"
              />
            </div>

            <div>
              <label className="overline">
                {es ? "Capacidades aplicables (taxonomía aprobada — quedan como PROPUESTAS)" : "Applicable capabilities (approved taxonomy — stored as PROPOSED)"}
              </label>
              <div className="mt-2 grid grid-cols-2 gap-x-4" style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--line)", padding: 10 }} data-testid="claim-capabilities">
                {taxonomy.map((c) => (
                  <label key={c} className="flex items-center gap-2 font-mono text-xs py-0.5 cursor-pointer">
                    <input type="checkbox" checked={caps.includes(c)} onChange={() => toggleCap(c)} data-testid={`claim-cap-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`} />
                    {c}
                  </label>
                ))}
              </div>
              <div className="mt-1 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                {es ? "Las categorías públicas no cambian automáticamente." : "Public categories never change automatically."}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Field label={es ? "Nombre de contacto *" : "Contact name *"} v={name} set={setName} required testid="claim-name" />
              <Field label={es ? "Puesto" : "Role"} v={role} set={setRole} testid="claim-role" />
              <Field label={es ? "Correo de trabajo *" : "Work email *"} v={email} set={setEmail} type="email" required testid="claim-email" />
              <Field label={es ? "Teléfono" : "Phone"} v={phone} set={setPhone} testid="claim-phone" />
            </div>

            <div>
              <label className="overline flex items-center gap-2">
                <Upload size={12} /> {es ? "Documento privado (opcional) — PDF/PNG/JPG/WEBP, máx 10 MB" : "Private document (optional) — PDF/PNG/JPG/WEBP, 10 MB max"}
              </label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={onFile} className="mt-2 font-mono text-xs" data-testid="claim-file" />
              {file ? <div className="font-mono text-xs mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</div> : null}
              {fileErr ? <div className="font-mono text-xs mt-1" style={{ color: "#dc2626" }} data-testid="claim-file-error">{fileErr}</div> : null}
              <div className="mt-1 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                {es ? "Nunca se publica ni se puede descargar públicamente." : "Never publicly displayed or downloadable."}
              </div>
            </div>

            {/* Honeypot — invisible to humans */}
            <input ref={honeypotRef} type="text" name="website" tabIndex={-1} autoComplete="off"
              style={{ position: "absolute", left: -9999, top: -9999, height: 1, width: 1, opacity: 0 }} aria-hidden="true" />

            {err ? (
              <div className="font-mono text-xs px-3 py-2" style={{ background: "#fee2e2", color: "#991b1b" }} data-testid="claim-error">
                {String(err)}
              </div>
            ) : null}

            <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
              {es
                ? "Al enviar recibirás un correo de confirmación. Esto no cambia el estatus de tu listado."
                : "After submitting you'll receive a confirmation email. This does not change your listing's status."}
            </div>

            <button type="submit" className="btn btn-primary w-full" disabled={submitting} data-testid="claim-submit">
              {submitting ? "…" : es ? "Enviar" : "Submit"}
            </button>
          </form>
        )}
      </div>
    </>
  );
}

function Field({ label, v, set, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input className="cd-input mt-2 w-full" style={{ fontSize: 16, minHeight: 44 }}
        type={type} value={v} onChange={(e) => set(e.target.value)} required={required} data-testid={testid} />
    </div>
  );
}

export default ClaimProfileSection;
